'use client';

import { useEffect, useState } from 'react';

/**
 * Mede volume do microfone do user (0-100).
 * Util pra speaking indicator + mic visualizer durante chamada.
 *
 * @param active se false, libera o microfone (nao pede permissao)
 */
export function useMicLevel(active: boolean): number {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!active) {
      setLevel(0);
      return;
    }

    let cancelled = false;
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let raf: number | null = null;

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        const AudioCtx =
          (window as unknown as {
            AudioContext: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }).AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        ctx = new AudioCtx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);

        const data = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          if (cancelled) return;
          analyser.getByteFrequencyData(data);
          const avg = data.reduce((a, b) => a + b, 0) / data.length;
          // *2 pra ficar mais sensivel em falas normais
          setLevel(Math.min(100, Math.round((avg / 255) * 100 * 2)));
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[webphone] sem permissao de microfone', err);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (ctx) ctx.close().catch(() => {});
    };
  }, [active]);

  return level;
}
